import { PaletteOptions } from '@mui/material/styles/createPalette';

import { Brand, getTokensByBrand } from '@newfold/tokens';

import { generatePalette, PaletteMode } from '../../themeDefault';
import {
  backgroundColors,
  borderColors,
  errorColors,
  infoColors,
  primaryColors,
  secondaryColors,
  successColors,
  textColors,
  warningColors,
} from './colors';

// Extend MUI Palette interfaces to include Portal Pixels custom colors
declare module '@mui/material/styles/createPalette' {
  interface PaletteOptions {
    border?: {
      light?: string;
      dark?: string;
      main?: string;
      disabled?: string;
    };
    placeholder?: {
      color?: string;
    };
  }

  interface Palette {
    border: {
      light: string;
      dark: string;
      main: string;
      disabled: string;
    };
    placeholder?: {
      color?: string;
    };
  }

  interface TypeText {
    placeholder?: string;
    buttonDisabled?: string;
    buttonTextDisabled?: string;
  }

  interface TypeBackground {
    primary: string;
    secondary: string;
    tertiary: string;
    quaternary: string;
    lightGrey: string;
    mediumGrey: string;
    lightBlue: string;
    mediumBlue: string;
    mediumDarkBlue: string;
    disabled: string;
  }
}

/**
 * Generate Portal Pixels Enhanced Palette
 *
 * For light mode: Uses only Portal Pixels colors (no merge with themeDefault)
 * For dark mode: Uses themeDefault colors (same for both themes)
 */
export const generatePortalPixelsPalette = (
  mode: PaletteMode,
  brand: Brand,
): PaletteOptions => {
  // For dark mode, return the base palette from themeDefault unchanged
  // Both themes use the same colors for dark mode
  if (mode === 'dark') {
    return generatePalette(mode, brand);
  }

  // For light mode: Create Portal Pixels palette from scratch (no merge)
  // Get tokens for the current brand
  const { color } = getTokensByBrand(brand);

  // Create complete Portal Pixels palette for light mode
  const portalPixelsPalette: PaletteOptions = {
    mode: 'light',

    text: {
      primary: textColors.primary,
      secondary: textColors.secondary,
      disabled: textColors.disabled,
      placeholder: textColors.placeholder,
      buttonDisabled: textColors.buttonDisabled,
      buttonTextDisabled: textColors.buttonTextDisabled,
    },

    placeholder: {
      color: textColors.placeholder,
    },

    background: {
      ...backgroundColors,
      default: backgroundColors.primary,
      paper: backgroundColors.primary,
      primary: backgroundColors.primary,
      secondary: backgroundColors.secondary,
      tertiary: backgroundColors.tertiary,
      quaternary: backgroundColors.quaternary,
      disabled: backgroundColors.disabled,
    },

    primary: {
      ...primaryColors,
      A100: primaryColors[200],
      A200: primaryColors[400],
      A400: primaryColors[600],
      A700: primaryColors[900],

      light: primaryColors.light,
      main: primaryColors.main,
      dark: primaryColors.dark,
    },

    secondary: {
      ...secondaryColors,
      A100: secondaryColors[200],
      A200: secondaryColors[400],
      A400: secondaryColors[600],
      A700: secondaryColors[900],

      main: secondaryColors.main,
      light: secondaryColors.light,
      dark: secondaryColors.dark,
    },

    info: {
      ...infoColors,
      A100: infoColors[200],
      A200: infoColors[400],
      A400: infoColors[600],
      A700: infoColors[900],
      main: infoColors.main,
      light: infoColors.light,
      dark: infoColors.dark,
    },

    success: {
      ...successColors,
      A100: successColors[200],
      A200: successColors[400],
      A400: successColors[600],
      A700: successColors[900],

      main: successColors.main,
      light: successColors.light,
      dark: successColors.dark,
    },

    error: {
      ...errorColors,
      A100: errorColors[200],
      A200: errorColors[400],
      A400: errorColors[600],
      A700: errorColors[900],

      main: errorColors.main,
      light: errorColors.light,
      dark: errorColors.dark,
    },

    warning: {
      ...warningColors,
      A100: warningColors[200],
      A200: warningColors[400],
      A400: warningColors[600],
      A700: warningColors[900],

      main: warningColors.main,
      light: warningColors.light,
      dark: warningColors.dark,
    },

    common: {
      black: color.semantic.neutral['1000'],
      white: color.semantic.neutral['0'],
    },

    grey: {
      ...color.spectrum.neutral,
      A100: color.spectrum.neutral[200],
      A200: color.spectrum.neutral[400],
      A400: color.spectrum.neutral[600],
      A700: color.spectrum.neutral[900],
    },

    border: {
      light: borderColors.light,
      dark: borderColors.dark,
      main: borderColors.main,
      disabled: color.spectrum.neutral['300'],
    },

    action: {
      hoverOpacity: 0,
      disabled: color.spectrum.slate['200'],
    },
  };

  return portalPixelsPalette;
};
